<!--
  @component DeploymentsLanding — the cross-project Deployments command center.
  tier:      screen (account-level; rail "Deployments" → /deployments)

  The operational counterpart to the Projects home: the live state of
  everything you've shipped, across every project. Composes the deployments
  blocks on a 12-col grid — stat strip · fleet map (the centerpiece) · live
  deploy stream · fleet status wall. Same canvas idea as the Projects home;
  later this can promote to the shared block-registry canvas + per-persona
  presets (org / dev / media).
-->
<script lang="ts">
  import { push } from "svelte-spa-router";
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useProvenanceIO } from "../../../../global/io/nodes/deployment/ProvenanceIO.svelte";
  import { projectRollup, accountStats } from "../../home/home-util.js";
  import { scopeProjects, scopeEvents } from "./fleet-util.js";
  import { useScopeIO, scopeToParam } from "./ScopeIO.svelte";
  import FleetMapBlock from "./FleetMapBlock.svelte";
  import FleetStatusWallBlock from "./FleetStatusWallBlock.svelte";
  import DeployStreamBlock from "./DeployStreamBlock.svelte";
  import ComposeCanvas from "./ComposeCanvas.svelte";

  const identity = useIdentityIO();
  const provenance = useProvenanceIO();
  const scope = useScopeIO();

  const stats = $derived.by(() => {
    const projects = scopeProjects(identity.projects, scope.projectIds);
    const events = scopeEvents(provenance.events, scope.projectIds);
    const roll = projectRollup(events);
    return accountStats(projects, events, roll);
  });
</script>

<div class="deployments">
  {#if !identity.ready}
    <div class="skeleton">
      <div class="sk-line lg"></div>
      <div class="sk-strip"></div>
      <div class="sk-canvas"></div>
    </div>
  {:else}
    <header class="page-head">
      <h1>Deployments</h1>
      <p class="lede">
        The live state of everything you've shipped — status, regions, and
        deploys across all your projects.
      </p>
    </header>

    <div class="strip">
      <div class="stat"><span class="num">{stats.projects}</span><span class="label">Projects</span></div>
      <div class="stat"><span class="num">{stats.live}</span><span class="label">Live</span></div>
      <div class="stat"><span class="num">{stats.deploys24h}</span><span class="label">Deploys · 24h</span></div>
      <div class="stat" class:warn={stats.issues > 0}>
        <span class="num">{stats.issues === 0 ? "✓" : stats.issues}</span>
        <span class="label">{stats.issues === 0 ? "All healthy" : "Need attention"}</span>
      </div>
    </div>

    <div class="canvas">
      <div class="col-main"><FleetMapBlock /></div>
      <div class="col-side"><DeployStreamBlock /></div>
      <div class="col-full">
        <section class="peek">
          <header class="peek-head">
            <div class="peek-title">
              <h2>Topology</h2>
              <p class="peek-sub">What's wired to what — databases, shared stores, and domain routes across your fleet.</p>
            </div>
            <button type="button" class="open" onclick={() => push(`/deployments/compose?p=${scopeToParam(scope.scope)}`)}>Open Compose →</button>
          </header>
          <ComposeCanvas peek />
        </section>
      </div>
      <div class="col-full"><FleetStatusWallBlock /></div>
    </div>
  {/if}
</div>

<style>
  .deployments {
    display: flex; flex-direction: column; gap: var(--space-5);
    width: 100%; max-width: 1280px; margin: 0 auto;
    font-family: var(--type-font);
  }
  .page-head { display: flex; flex-direction: column; gap: var(--space-1); }
  h1 { margin: 0; font-size: 24px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .lede { margin: 0; font-size: 14px; color: var(--text-secondary); line-height: 1.55; max-width: 64ch; }

  .strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: var(--space-3); }
  .stat {
    display: flex; flex-direction: column; gap: 4px;
    padding: var(--space-4) var(--space-4) var(--space-3);
    background: var(--glass-fill); border: 0.5px solid var(--border);
    border-radius: var(--radius-lg, 14px);
  }
  .num { font-size: 28px; font-weight: 600; letter-spacing: -0.02em; line-height: 1; color: var(--text-primary); font-variant-numeric: tabular-nums; }
  .label { font-size: 11px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-tertiary); }
  .stat.warn .num { color: var(--warning, #e6b450); }
  .stat.warn { border-color: color-mix(in oklab, var(--warning, #e6b450) 35%, var(--border)); }

  .canvas { display: grid; grid-template-columns: repeat(12, 1fr); gap: var(--space-4); align-items: start; }
  .col-main { grid-column: span 8; min-width: 0; }
  .col-side { grid-column: span 4; min-width: 0; }
  .col-full { grid-column: 1 / -1; min-width: 0; }

  .peek { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-4); background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-xl, 18px); }
  .peek-head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--space-3); }
  .peek-title { min-width: 0; }
  .peek-title h2 { margin: 0; font-size: 16px; font-weight: 600; letter-spacing: -0.01em; color: var(--text-primary); }
  .peek-sub { margin: 2px 0 0; font-size: 12.5px; color: var(--text-secondary); line-height: 1.5; }
  .open { appearance: none; background: transparent; border: 0.5px solid var(--border-strong); border-radius: var(--radius-md); padding: 6px var(--space-3); font: inherit; font-size: 12.5px; color: var(--text-secondary); cursor: pointer; white-space: nowrap; transition: background var(--ease-fast), border-color var(--ease-fast), color var(--ease-fast); }
  .open:hover { background: var(--bg-hover); color: var(--text-primary); border-color: var(--border-focus); }

  @media (max-width: 960px) {
    .col-main, .col-side { grid-column: 1 / -1; }
    .strip { grid-template-columns: repeat(2, 1fr); }
  }

  /* Skeleton */
  .skeleton { display: flex; flex-direction: column; gap: var(--space-4); }
  .sk-line.lg { height: 28px; width: 220px; border-radius: var(--radius-md); background: var(--bg-hover); }
  .sk-strip { height: 78px; border-radius: var(--radius-lg, 14px); background: var(--bg-hover); }
  .sk-canvas { height: 420px; border-radius: var(--radius-xl, 18px); background: var(--bg-hover); }
  .skeleton > * { animation: shimmer 1.4s ease-in-out infinite; }
  @keyframes shimmer { 0%,100% { opacity: 0.5; } 50% { opacity: 0.85; } }
  @media (prefers-reduced-motion: reduce) { .skeleton > * { animation: none; } }
</style>
